import { useCallback, useEffect, useState } from 'react';
import { Box, Card, Stack, TextField, Typography } from '@mui/material';
import StickyNote2Rounded from '@mui/icons-material/StickyNote2Rounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import ShoppingCartRounded from '@mui/icons-material/ShoppingCartRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import {
  getReportsOverview, getPaymentsTrend, getTopPaymentCategories, ReportMetric,
} from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import TabFilterBar from '../components/ui/TabFilterBar';
import StatCard from '../components/ui/StatCard';
import TrendBarChart, { TrendDatum } from '../components/ui/charts/TrendBarChart';
import DonutChart, { DonutDatum } from '../components/ui/charts/DonutChart';

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'payments', label: 'Payments' },
  { value: 'grocery', label: 'Grocery' },
  { value: 'documents', label: 'Documents' },
];

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const emptyMetric: ReportMetric = { count: 0, deltaPct: null };

const ReportsPage = () => {
  const [tab, setTab] = useState('overview');
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [overview, setOverview] = useState<{ notes: ReportMetric; payments: ReportMetric; groceryItems: ReportMetric; documents: ReportMetric }>({
    notes: emptyMetric, payments: emptyMetric, groceryItems: emptyMetric, documents: emptyMetric,
  });
  const [trend, setTrend] = useState<TrendDatum[]>([]);
  const [categories, setCategories] = useState<DonutDatum[]>([]);

  const load = useCallback(async () => {
    try {
      const [ov, tr, cats] = await Promise.all([
        getReportsOverview(from, to),
        getPaymentsTrend(from, to),
        getTopPaymentCategories(from, to),
      ]);
      setOverview(ov);
      setTrend(tr);
      setCategories(cats.map((c) => ({ name: c.name, value: c.value })));
    } catch {
      // keep previous state on failure
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <PageHeader
        title="Reports"
        actions={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField type="date" size="small" label="From" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField type="date" size="small" label="To" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Stack>
        }
      />

      <TabFilterBar value={tab} onChange={setTab} options={TABS} />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2.5, mb: 3 }}>
        <StatCard icon={StickyNote2Rounded} label="Notes" value={overview.notes.count} deltaPct={overview.notes.deltaPct ?? undefined} color="#6226A7" />
        <StatCard icon={PaymentsRounded} label="Payments" value={overview.payments.count} deltaPct={overview.payments.deltaPct ?? undefined} color="#0277bd" />
        <StatCard icon={ShoppingCartRounded} label="Grocery Items" value={overview.groceryItems.count} deltaPct={overview.groceryItems.deltaPct ?? undefined} color="#F6A938" />
        <StatCard icon={FolderRounded} label="Documents" value={overview.documents.count} deltaPct={overview.documents.deltaPct ?? undefined} color="#CF1E52" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2.5 }}>
        <Card sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Payments Trend</Typography>
          <TrendBarChart data={trend} />
        </Card>
        <Card sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Top Categories (Payments)</Typography>
          <DonutChart data={categories} />
        </Card>
      </Box>
    </Box>
  );
};

export default ReportsPage;
